import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Table,
  Spinner,
  Alert,
  Modal,
  Form,
  Badge,
  Row,
  Col,
} from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaShieldAlt, FaArrowLeft, FaTimes } from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import ObjectConfigApi from '../../api/ObjectConfigApi';
import { listCustomScriptsForObject } from '../../api/CustomScriptApi';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import {
  standardLabelStyles,
  standardInputStyles,
  standardInputContainerHeight,
} from '../../utils/conditionInputStyles';
import LookupLayout from '../layout/LookupLayout';
import MultiSelectDropdown from '../layout/MultiSelectDropdown1';
import SharingRuleCriteriaConfig, {
  SharingCriteriaLogicSelect,
} from '../setup/sharingrule/SharingRuleCriteriaConfig';

function labelToApiName(label) {
  if (label == null || typeof label !== 'string') return '';
  let s = label.trim().replace(/\s+/g, '_');
  s = s.replace(/[^A-Za-z0-9_]+/g, '_');
  s = s.replace(/_+/g, '_').replace(/^_|_$/g, '');
  if (/^[0-9]/.test(s)) s = `_${s}`;
  if (s.length > 100) s = s.slice(0, 100).replace(/_+$/g, '');
  return s.toLowerCase();
}

/** Default numbered expression when switching to custom criteria logic (same as workflows). */
function defaultCustomCriteriaExpression(validCriteriaCount) {
  if (validCriteriaCount <= 0) return '1';
  if (validCriteriaCount === 1) return '1';
  return Array.from({ length: validCriteriaCount }, (_, i) => String(i + 1)).join(' AND ');
}

const emptyForm = () => ({
  name: '',
  label: '',
  description: '',
  /** Selected trigger keys; persisted as `trigger_rule` JSON array on the server */
  triggerRule: ['on_create'],
  error_message: '',
  error_field: '',
  is_active: true,
  execution_order: 1,
  stop_on_failure: true,
  is_standard: false,
  is_editable: true,
  customScriptId: '',
});

const emptyCriterion = () => ({
  fieldName: '',
  operator: '',
  value: '',
});

/** Keys stored in `trigger_rule` JSON (same naming as the rest of object validation) */
const TRIGGER_PICKLIST = [
  { key: 'on_create', value: 'On Create' },
  { key: 'on_update', value: 'On Update' },
  { key: 'on_delete', value: 'On Delete' },
];

const ALLOWED_TRIGGER_RULE_KEYS = new Set(TRIGGER_PICKLIST.map((o) => o.key));

function filterAllowedTriggerRuleKeys(arr) {
  const out = (arr || [])
    .map((x) => String(x).trim())
    .filter((k) => ALLOWED_TRIGGER_RULE_KEYS.has(k));
  return out.length ? out : ['on_create'];
}

function labelForTriggerKey(k) {
  const f = TRIGGER_PICKLIST.find((o) => String(o.key) === String(k));
  return f ? f.value : String(k);
}

function parseTriggerRuleFromRow(row) {
  const raw = row?.trigger_rule != null ? row.trigger_rule : row?.trigger_event;
  if (raw == null) return ['on_create'];
  if (Array.isArray(raw)) {
    return raw.length
      ? filterAllowedTriggerRuleKeys(raw.map((x) => String(x)))
      : ['on_create'];
  }
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return ['on_create'];
    try {
      const p = JSON.parse(t);
      if (Array.isArray(p) && p.length) {
        return filterAllowedTriggerRuleKeys(p.map((x) => String(x)));
      }
    } catch {
      return filterAllowedTriggerRuleKeys([t]);
    }
  }
  if (typeof raw === 'object' && raw !== null) {
    return ['on_create'];
  }
  return ['on_create'];
}

function formatTriggerCell(row) {
  return parseTriggerRuleFromRow(row).map(labelForTriggerKey).join(', ');
}

/** System/audit field API names — not offerable for inline error field (id, dates, user refs, etc.). */
const ERROR_FIELD_PICKER_EXCLUDED_NAMES_LOWER = new Set([
  'id',
  'createddate',
  'lastmodifieddate',
  'createdbyid',
  'lastmodifiedbyid',
  'ownerid',
  'is_deleted',
]);

function isTruthyConfigFlag(v) {
  return v === true || v === 'true' || v === 1 || v === '1';
}

/**
 * Excludes read-only fields, is_system, and known system / audit API names
 * (aligned with FieldManager and workflow “target field” rules).
 */
function isExcludedFromErrorFieldPicker(f) {
  if (!f) return true;
  const api = String(f.field_name || f.name || '').trim();
  if (!api) return true;
  if (ERROR_FIELD_PICKER_EXCLUDED_NAMES_LOWER.has(api.toLowerCase())) return true;
  if (isTruthyConfigFlag(f.is_readonly)) return true;
  if (f.readonly === true) return true;
  if (isTruthyConfigFlag(f.is_system)) return true;
  if (isTruthyConfigFlag(f.is_system_field)) return true;
  return false;
}

/** Same lookup objects as sharing-rule grants (user, role, user_group). */
function grantLookupMeta(targetType) {
  switch (String(targetType || '').toUpperCase()) {
    case 'ROLE':
      return {
        objectName: 'role',
        searchFields: 'name',
        displayFields: 'name',
        placeholder: 'Search roles…',
      };
    case 'TEAM':
      return {
        objectName: 'user_group',
        searchFields: 'name,description',
        displayFields: 'name',
        placeholder: 'Search user groups…',
      };
    case 'USER':
    default:
      return {
        objectName: 'user',
        searchFields: 'firstname,lastname,email,username',
        displayFields: 'firstname,lastname',
        placeholder: 'Search users…',
      };
  }
}

function normalizeContextTargetType(t) {
  const u = String(t || 'USER').toUpperCase();
  if (u === 'ROLE' || u === 'TEAM' || u === 'USER') return u;
  return 'USER';
}

function grantTypeShortLabel(type) {
  switch (String(type || '').toUpperCase()) {
    case 'ROLE':
      return 'Role';
    case 'TEAM':
      return 'Group';
    case 'USER':
    default:
      return 'User';
  }
}

function contextGrantChipLabel(g) {
  const label = String(g.target_label || '').trim() || g.target_id;
  return `${grantTypeShortLabel(g.target_type)} · ${label}`;
}

/** Distinct chip colors: user (primary), role (success), user group (warning). */
function getContextGrantChipBadgeProps(targetType) {
  const t = normalizeContextTargetType(targetType);
  const base =
    'd-inline-flex align-items-center rounded-1 ps-2 pe-1 py-1 fw-normal border-0';
  if (t === 'ROLE') {
    return { bg: 'success', className: base };
  }
  if (t === 'TEAM') {
    return { bg: 'warning', text: 'dark', className: base };
  }
  return { bg: 'primary', className: base };
}

function dedupeGrants(list) {
  const seen = new Set();
  const out = [];
  for (const g of list || []) {
    const id = g?.target_id != null ? String(g.target_id).trim() : '';
    if (!id) continue;
    const tt = normalizeContextTargetType(g.target_type);
    const k = `${tt}:${id}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({
      target_type: tt,
      target_id: id,
      target_label: g.target_label != null ? String(g.target_label) : '',
    });
  }
  return out;
}

/** Load rule_context. Expects { grants: [...] } (same as sharing). Returns deduped list, may be empty. */
function parseRuleContextGrants(raw) {
  if (raw == null) return [];
  let o = raw;
  if (typeof o === 'string' && o.trim()) {
    try {
      o = JSON.parse(o);
    } catch {
      return [];
    }
  }
  if (!o || typeof o !== 'object' || Array.isArray(o)) {
    return [];
  }
  const list = o.grants || o.access;
  if (!Array.isArray(list) || !list.length) {
    return [];
  }
  return dedupeGrants(
    list.map((g) => ({
      target_type: normalizeContextTargetType(g.target_type),
      target_id: g.target_id != null ? String(g.target_id) : '',
      target_label: g.target_label != null ? String(g.target_label) : '',
    }))
  );
}

function buildRuleContextPayload(grants) {
  const rows = dedupeGrants(grants);
  if (rows.length === 0) return null;
  return {
    grants: rows.map((r) => {
      const g = { target_type: r.target_type, target_id: r.target_id };
      if (r.target_label) g.target_label = r.target_label;
      return g;
    }),
  };
}

function normalizeCriterionRow(c) {
  if (!c || typeof c !== 'object') return emptyCriterion();
  return {
    fieldName: c.fieldName || c.field || '',
    operator: c.operator || '',
    value: c.value !== undefined && c.value !== null ? c.value : '',
  };
}

/** True if criterion uses a previous-record operator (object validation `old_*`). */
function criterionUsesOldValueOperator(c) {
  const op = c?.operator;
  if (op == null || op === '') return false;
  return String(op).toLowerCase().replace(/\s+/g, '_').startsWith('old_');
}

/**
 * Map stored `conditions` JSON to builder state.
 * Accepts the same shape as sharing rules / workflow-style criteria: { logic, criteria[] }.
 */
function parseConditionsForForm(raw) {
  const empty = {
    criteriaList: [emptyCriterion()],
    criteriaLogic: 'AND',
    criteriaCustomExpression: '',
    hadCustomOrLegacy: false,
  };
  if (raw == null) return empty;
  let cj = raw;
  if (typeof cj === 'string') {
    try {
      cj = JSON.parse(cj);
    } catch {
      return { ...empty, hadCustomOrLegacy: true };
    }
  }
  if (!cj || typeof cj !== 'object' || Array.isArray(cj)) {
    return { ...empty, hadCustomOrLegacy: true };
  }

  const logicRaw = String(cj.logic ?? '').trim();
  const logicUpper = logicRaw.toUpperCase();

  // Sharing-rule style: logic === 'CUSTOM' + customLogic string
  if (logicUpper === 'CUSTOM') {
    const cl =
      typeof cj.customLogic === 'string' && cj.customLogic.trim()
        ? cj.customLogic.trim()
        : '';
    if (!cl || !Array.isArray(cj.criteria)) {
      return { ...empty, hadCustomOrLegacy: true };
    }
    const criteriaList =
      cj.criteria.length > 0
        ? cj.criteria.map(normalizeCriterionRow)
        : [emptyCriterion()];
    return {
      criteriaList,
      criteriaLogic: 'CUSTOM',
      criteriaCustomExpression: cl,
      hadCustomOrLegacy: false,
    };
  }

  if (!Array.isArray(cj.criteria)) {
    if (Object.keys(cj).length > 0) {
      return { ...empty, hadCustomOrLegacy: true };
    }
    return empty;
  }

  const criteriaList =
    cj.criteria.length > 0
      ? cj.criteria.map(normalizeCriterionRow)
      : [emptyCriterion()];

  if (logicUpper === 'AND' || logicUpper === 'OR') {
    return {
      criteriaList,
      criteriaLogic: logicUpper,
      criteriaCustomExpression: '',
      hadCustomOrLegacy: false,
    };
  }

  if (logicRaw !== '') {
    return {
      criteriaList,
      criteriaLogic: 'CUSTOM',
      criteriaCustomExpression: logicRaw,
      hadCustomOrLegacy: false,
    };
  }

  return {
    criteriaList,
    criteriaLogic: 'AND',
    criteriaCustomExpression: '',
    hadCustomOrLegacy: false,
  };
}

const ObjectValidationManager = ({ objectName, objectLabel, onBack }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  const [nameManualOverride, setNameManualOverride] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [objectFields, setObjectFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [criteriaList, setCriteriaList] = useState([emptyCriterion()]);
  const [criteriaLogic, setCriteriaLogic] = useState('AND');
  const [criteriaCustomExpression, setCriteriaCustomExpression] = useState('');
  const [conditionsParseWarning, setConditionsParseWarning] = useState('');
  const [ruleContextGrants, setRuleContextGrants] = useState([]);
  const [contextPickerType, setContextPickerType] = useState('USER');
  const [contextLookupNonce, setContextLookupNonce] = useState(0);
  const [ruleContextDupHint, setRuleContextDupHint] = useState('');
  const [validationScriptOptions, setValidationScriptOptions] = useState([]);
  const [loadingValScripts, setLoadingValScripts] = useState(false);

  const contextPickerLookup = useMemo(
    () => grantLookupMeta(contextPickerType),
    [contextPickerType]
  );

  const errorFieldOptions = useMemo(() => {
    const raw = objectFields || [];
    const rows = raw
      .filter((f) => !isExcludedFromErrorFieldPicker(f))
      .map((f) => {
        const api = String(f.field_name || f.name || '').trim();
        if (!api) return null;
        const label = String(
          f.field_label || f.label || f.field_name || f.name || api
        ).trim();
        return { api, label: label || api };
      })
      .filter(Boolean);
    rows.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
    );
    return rows;
  }, [objectFields]);

  const title = useMemo(
    () => objectLabel || objectName,
    [objectLabel, objectName]
  );

  const loadObjectFields = useCallback(async () => {
    if (!objectName) {
      setObjectFields([]);
      return;
    }
    setLoadingFields(true);
    try {
      const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(
        objectName
      );
      let fields = [];
      if (response?.success && response?.data) {
        fields = Array.isArray(response.data.fields) ? response.data.fields : [];
      } else if (response?.data?.success && response?.data?.data) {
        fields = Array.isArray(response.data.data.fields)
          ? response.data.data.fields
          : [];
      }
      setObjectFields(fields);
    } catch {
      setObjectFields([]);
    } finally {
      setLoadingFields(false);
    }
  }, [objectName]);

  useEffect(() => {
    loadObjectFields();
  }, [loadObjectFields]);

  useEffect(() => {
    if (!showModal || !objectName) {
      setValidationScriptOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingValScripts(true);
      try {
        const res = await listCustomScriptsForObject(objectName, {
          scriptTypes: ['validation'],
        });
        if (cancelled) return;
        if (res?.success && Array.isArray(res.data)) {
          setValidationScriptOptions(res.data);
        } else {
          setValidationScriptOptions([]);
        }
      } catch {
        if (!cancelled) setValidationScriptOptions([]);
      } finally {
        if (!cancelled) setLoadingValScripts(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showModal, objectName]);

  const load = useCallback(async () => {
    if (!objectName) return;
    setLoading(true);
    setError('');
    try {
      const api = new DataApi('object-validation');
      const res = await api.get(
        `/object/${encodeURIComponent(objectName)}?activeOnly=false`
      );
      if (res?.data?.success) {
        setRows(res.data.data || []);
      } else {
        setRows([]);
        setError(res?.data?.message || 'Failed to load validation rules');
      }
    } catch (e) {
      setRows([]);
      setError(getApiErrorMessage(e, 'Failed to load validation rules'));
    } finally {
      setLoading(false);
    }
  }, [objectName]);

  useEffect(() => {
    load();
  }, [load]);

  const addCriterion = () => {
    setCriteriaList((prev) => [...(prev || []), emptyCriterion()]);
  };

  const deleteCriterion = (index) => {
    setCriteriaList((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [emptyCriterion()];
    });
  };

  const updateCriterion = (index, key, value) => {
    setCriteriaList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [key]: value };
      return updated;
    });
  };

  const onCriteriaLogicModeChange = (v) => {
    const validCount = (criteriaList || []).filter((c) => c.fieldName && c.operator).length;
    if (v === 'CUSTOM') {
      setCriteriaLogic('CUSTOM');
      setCriteriaCustomExpression((prev) =>
        String(prev || '').trim() ? prev : defaultCustomCriteriaExpression(validCount)
      );
    } else {
      setCriteriaLogic(v);
      setCriteriaCustomExpression('');
    }
  };

  const removeRuleContextGrant = (idx) => {
    setRuleContextGrants((prev) => (prev || []).filter((_, i) => i !== idx));
  };

  const buildConditionsObject = () => {
    const criteria = (criteriaList || [])
      .map(normalizeCriterionRow)
      .filter((c) => c.fieldName && c.operator);
    if (criteria.length < 1) {
      throw new Error(
        'Add at least one row with a field and operator, or use Delete to remove empty rows.'
      );
    }
    if (criteriaLogic === 'CUSTOM') {
      const expr = String(criteriaCustomExpression || '').trim();
      if (!expr) {
        throw new Error(
          'Enter a custom criteria expression (e.g. (1 OR 2) AND 3). Numbers refer to rows below.'
        );
      }
      return { logic: expr, criteria };
    }
    return {
      logic: criteriaLogic === 'OR' ? 'OR' : 'AND',
      criteria,
    };
  };

  const openCreate = () => {
    setEditingId(null);
    setEditingRow(null);
    setNameManualOverride(false);
    setModalError('');
    setConditionsParseWarning('');
    setCriteriaLogic('AND');
    setCriteriaCustomExpression('');
    setCriteriaList([emptyCriterion()]);
    setRuleContextGrants([]);
    setContextPickerType('USER');
    setContextLookupNonce(0);
    setRuleContextDupHint('');
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setEditingRow(row);
    setNameManualOverride(true);
    setModalError('');
    const parsed = parseConditionsForForm(row.conditions);
    setCriteriaList(parsed.criteriaList);
    setCriteriaLogic(parsed.criteriaLogic);
    setCriteriaCustomExpression(parsed.criteriaCustomExpression ?? '');
    setConditionsParseWarning(
      parsed.hadCustomOrLegacy
        ? 'This rule’s conditions were not in the field/operator format (or used custom logic). Rebuild the conditions below. Saving will replace the stored format.'
        : ''
    );
    setRuleContextGrants(parseRuleContextGrants(row.rule_context));
    setContextPickerType('USER');
    setContextLookupNonce(0);
    setRuleContextDupHint('');
    setForm({
      name: row.name || '',
      label: row.label || '',
      description: row.description || '',
      triggerRule: parseTriggerRuleFromRow(row),
      error_message: row.error_message || '',
      error_field: row.error_field || '',
      is_active: row.is_active !== false,
      execution_order:
        row.execution_order != null ? Number(row.execution_order) : 1,
      stop_on_failure: row.stop_on_failure !== false,
      is_standard: !!row.is_standard,
      is_editable: row.is_editable !== false,
      customScriptId: row.custom_script_id != null ? String(row.custom_script_id) : '',
    });
    setShowModal(true);
  };

  const onChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (!editingId && !nameManualOverride && field === 'label') {
        next.name = labelToApiName(value);
      }
      return next;
    });
  };

  const buildPayload = () => {
    const useScript = String(form.customScriptId || '').trim() !== '';
    const conditions = useScript
      ? { logic: 'AND', criteria: [] }
      : buildConditionsObject();
    const trigger_rule = (Array.isArray(form.triggerRule) ? form.triggerRule : [])
      .map((x) => String(x == null ? '' : x).trim())
      .filter(Boolean);
    if (trigger_rule.length < 1) {
      throw new Error('Select at least one trigger event.');
    }

    if (!useScript) {
      const crit = Array.isArray(conditions.criteria) ? conditions.criteria : [];
      if (crit.some(criterionUsesOldValueOperator) && !trigger_rule.includes('on_update')) {
        throw new Error(
          'Old-value operators (e.g. Old =, Old Is Null) require the rule to include the On Update trigger.'
        );
      }
    }

    const ruleContext = buildRuleContextPayload(ruleContextGrants);

    return {
      object_name: objectName,
      name: String(form.name || '').trim(),
      label: String(form.label || '').trim(),
      description: String(form.description || '').trim() || null,
      trigger_rule,
      conditions,
      error_message: String(form.error_message || '').trim(),
      error_field: String(form.error_field || '').trim() || null,
      is_active: !!form.is_active,
      rule_context: ruleContext,
      execution_order: Number.isFinite(Number(form.execution_order))
        ? Number(form.execution_order)
        : 1,
      stop_on_failure: !!form.stop_on_failure,
      is_standard: !!form.is_standard,
      is_editable: form.is_editable !== false,
      custom_script_id: useScript ? String(form.customScriptId).trim() : null,
    };
  };

  const handleSave = async () => {
    setSaving(true);
    setModalError('');
    let payload;
    try {
      payload = buildPayload();
    } catch (e) {
      setModalError(e.message || 'Invalid form');
      setSaving(false);
      return;
    }

    try {
      const api = new DataApi('object-validation');
      if (editingId) {
        const res = await api.update(payload, editingId);
        if (res?.data?.success) {
          setSuccess('Validation rule updated.');
          setTimeout(() => setSuccess(''), 3000);
          setShowModal(false);
          load();
        } else {
          setModalError(res?.data?.message || 'Update failed');
        }
      } else {
        const res = await api.create(payload);
        if (res?.data?.success) {
          setSuccess('Validation rule created.');
          setTimeout(() => setSuccess(''), 3000);
          setShowModal(false);
          load();
        } else {
          setModalError(res?.data?.message || 'Create failed');
        }
      }
    } catch (e) {
      setModalError(
        getApiErrorMessage(
          e,
          editingId ? 'Failed to update validation rule' : 'Failed to create validation rule'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (row.is_editable === false) {
      return;
    }
    if (!window.confirm(`Delete validation rule "${row.label || row.name}"?`)) {
      return;
    }
    setError('');
    try {
      const api = new DataApi('object-validation');
      const res = await api.delete(row.id);
      if (res?.data?.success) {
        setSuccess('Validation rule deleted.');
        setTimeout(() => setSuccess(''), 3000);
        load();
      } else {
        setError(res?.data?.message || 'Delete failed');
      }
    } catch (e) {
      setError(getApiErrorMessage(e, 'Failed to delete validation rule'));
    }
  };

  const readOnly = editingRow && editingRow.is_editable === false;
  const scriptMode = String(form.customScriptId || '').trim() !== '';
  const hasOldValueOpInCriteria = useMemo(
    () => (criteriaList || []).some(criterionUsesOldValueOperator),
    [criteriaList]
  );
  const triggerHasOnUpdate =
    Array.isArray(form.triggerRule) && form.triggerRule.includes('on_update');

  const onContextPickerSelect = (id, label) => {
    if (readOnly) return;
    if (!id) return;
    const tt = normalizeContextTargetType(contextPickerType);
    const idStr = String(id).trim();
    const list = ruleContextGrants || [];
    const dup = list.some(
      (g) =>
        normalizeContextTargetType(g.target_type) === tt &&
        String(g.target_id || '').trim() === idStr
    );
    if (dup) {
      setRuleContextDupHint('This user, role, or group is already added.');
      return;
    }
    setRuleContextDupHint('');
    setRuleContextGrants([
      ...list,
      {
        target_type: tt,
        target_id: idStr,
        target_label: (label && String(label).trim()) || '',
      },
    ]);
    setContextLookupNonce((n) => n + 1);
  };

  useEffect(() => {
    if (!ruleContextDupHint) return;
    const t = setTimeout(() => setRuleContextDupHint(''), 3000);
    return () => clearTimeout(t);
  }, [ruleContextDupHint]);

  return (
    <div className="object-validation-manager">
      <div className="view-header">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={onBack}
              className="mb-3"
            >
              <FaArrowLeft className="me-1" />
              Back to Object
            </Button>
            <h2>
              <FaShieldAlt className="me-3" />
              Validation rules — {title}
            </h2>
            <p className="text-muted mb-0">
              Block saves with field criteria and/or a <code>validation</code> custom script (per
              object). Script rules run in the sandbox with the merged record and, on update, the
              previous row.
            </p>
          </div>
          <Button variant="primary" onClick={openCreate} disabled={!objectName}>
            <FaPlus className="me-1" />
            New rule
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" className="me-2" />
          Loading rules…
        </div>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover size="sm" align="middle">
            <thead>
              <tr>
                <th>Label</th>
                <th>API name</th>
                <th>Trigger</th>
                <th>Order</th>
                <th>Mode</th>
                <th>Active</th>
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">
                    No validation rules for this object yet.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.label || row.name}</strong>
                      {row.is_standard && (
                        <Badge bg="secondary" className="ms-2">
                          Standard
                        </Badge>
                      )}
                    </td>
                    <td>
                      <code>{row.name}</code>
                    </td>
                    <td className="small">{formatTriggerCell(row)}</td>
                    <td>{row.execution_order}</td>
                    <td>
                      {row.custom_script_id ? (
                        <Badge bg="info">Script</Badge>
                      ) : (
                        <Badge bg="secondary">Criteria</Badge>
                      )}
                    </td>
                    <td>
                      {row.is_active ? (
                        <Badge bg="success">Yes</Badge>
                      ) : (
                        <Badge bg="secondary">No</Badge>
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => openEdit(row)}
                          title="Edit"
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(row)}
                          disabled={row.is_editable === false}
                          title={
                            row.is_editable === false
                              ? 'This rule cannot be deleted'
                              : 'Delete'
                          }
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xl"
        centered
        backdrop="static"
        scrollable
      >
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="h6 mb-0">
            {editingId ? 'Edit validation rule' : 'New validation rule'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-2 px-3">
          {readOnly && (
            <Alert variant="warning" className="py-2 px-3 mb-2 small">
              This rule is not editable in the UI.
            </Alert>
          )}
          {conditionsParseWarning && (
            <Alert
              variant="warning"
              className="py-2 px-3 mb-2 small"
              dismissible
              onClose={() => setConditionsParseWarning('')}
            >
              {conditionsParseWarning}
            </Alert>
          )}
          {modalError && (
            <Alert variant="danger" className="py-2 px-3 mb-2 small" onClose={() => setModalError('')}>
              {modalError}
            </Alert>
          )}

          <Row className="g-2 mb-1">
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small mb-1">Label *</Form.Label>
                <Form.Control
                  size="sm"
                  value={form.label}
                  onChange={(e) => onChange('label', e.target.value)}
                  disabled={readOnly}
                  placeholder="e.g. Amount must be positive"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small mb-1">API name *</Form.Label>
                <Form.Control
                  size="sm"
                  value={form.name}
                  onChange={(e) => {
                    setNameManualOverride(true);
                    onChange('name', e.target.value);
                  }}
                  disabled={readOnly}
                  placeholder="e.g. amount_positive"
                />
                <Form.Text className="text-muted small" style={{ fontSize: '0.75rem' }}>
                  Unique per object; autofills from label until changed.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={1}
              size="sm"
              value={form.description}
              onChange={(e) => onChange('description', e.target.value)}
              disabled={readOnly}
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Check
              type="checkbox"
              id="ov-is-standard"
              className="small"
              label="Standard rule (template)"
              checked={form.is_standard}
              onChange={(e) => onChange('is_standard', e.target.checked)}
              disabled={readOnly}
            />
          </Form.Group>

          <Row className="g-2 mb-2">
            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label className="small mb-1">Trigger *</Form.Label>
                <Form.Text className="text-muted d-block small mb-1" style={{ fontSize: '0.75rem' }}>
                  Stored as <code>trigger_rule</code> JSON.
                </Form.Text>
                <MultiSelectDropdown
                  selectedValues={Array.isArray(form.triggerRule) ? form.triggerRule : []}
                  picklistValues={TRIGGER_PICKLIST}
                  onChange={(selected) => onChange('triggerRule', selected)}
                  disabled={readOnly}
                  placeholder="Events"
                  isRequired={!readOnly}
                />
                {Array.isArray(form.triggerRule) && form.triggerRule.length === 0 && (
                  <Form.Text className="text-muted mt-1 d-block small">Select at least one</Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label className="small mb-1">Validation script</Form.Label>
                <Form.Text className="text-muted d-block small mb-1" style={{ fontSize: '0.75rem' }}>
                  Optional. <code>script_type = validation</code>, <code>params.validation</code>.
                </Form.Text>
                {loadingValScripts && (
                  <div className="text-muted small py-1">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading…
                  </div>
                )}
                <Form.Select
                  size="sm"
                  value={form.customScriptId || ''}
                  onChange={(e) => onChange('customScriptId', e.target.value)}
                  disabled={readOnly}
                >
                  <option value="">— Criteria only —</option>
                  {validationScriptOptions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.display_label || s.script_key || s.id}
                      {s.script_key ? ` (${s.script_key})` : ''}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {scriptMode && (
            <Alert variant="info" className="py-1 px-2 mb-2 small">
              Script selected — field criteria below are ignored.
            </Alert>
          )}

          <div className="mb-2 border-top pt-2">
            <Form.Label className="fw-semibold small mb-1" style={standardLabelStyles}>
              Conditions {scriptMode ? '' : '*'} (if true → block save)
            </Form.Label>
            <p className="text-muted small mb-2" style={{ fontSize: '0.8rem', lineHeight: 1.35 }}>
              Field rows + AND / OR / custom expression (workflows / list filters).{' '}
              <code className="small">conditions.logic</code>,{' '}
              <code className="small">criteria</code>.
            </p>

            <Row className="g-2 mb-1">
              <Col md={5} lg={4}>
                <SharingCriteriaLogicSelect
                  value={criteriaLogic}
                  onChange={onCriteriaLogicModeChange}
                  readOnly={readOnly || scriptMode}
                  compact
                />
              </Col>
            </Row>
            {criteriaLogic === 'CUSTOM' && (
              <Form.Group className="mb-2">
                <Form.Label className="small text-muted mb-0">Expression</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={1}
                  size="sm"
                  value={criteriaCustomExpression}
                  onChange={(e) => setCriteriaCustomExpression(e.target.value)}
                  disabled={readOnly || scriptMode}
                  placeholder="(1 OR 2) AND 3"
                  className="font-monospace small"
                  style={standardInputStyles}
                />
                <Form.Text className="text-muted small" style={{ fontSize: '0.75rem' }}>
                  Row numbers 1…n, AND / OR / parentheses.
                </Form.Text>
              </Form.Group>
            )}

            {loadingFields ? (
              <div className="text-muted small py-2">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading fields…
              </div>
            ) : objectFields.length === 0 ? (
              <Alert variant="warning" className="mb-0 py-2 small">
                No fields loaded for this object.
              </Alert>
            ) : (
              <>
                {hasOldValueOpInCriteria && !triggerHasOnUpdate && !scriptMode && (
                  <Form.Text className="text-warning small d-block mb-1">
                    Old-value criteria require &quot;On Update&quot; in Trigger.
                  </Form.Text>
                )}
                <SharingRuleCriteriaConfig
                  criteriaList={criteriaList}
                  onAdd={addCriterion}
                  onDelete={deleteCriterion}
                  onChange={updateCriterion}
                  objectFields={objectFields}
                  readOnly={readOnly}
                  disabled={readOnly || scriptMode}
                  includeIsChangedOperator
                  includeOldValueOperators={triggerHasOnUpdate}
                  compact
                />
              </>
            )}
          </div>

          <Row className="g-2 mb-2">
            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label className="small mb-1">Error message *</Form.Label>
                <Form.Control
                  size="sm"
                  value={form.error_message}
                  onChange={(e) => onChange('error_message', e.target.value)}
                  disabled={readOnly}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label className="small mb-1">Error field</Form.Label>
                {loadingFields ? (
                  <div className="text-muted small py-1">
                    <Spinner animation="border" size="sm" className="me-2" />
                    …
                  </div>
                ) : (
                  <Form.Select
                    size="sm"
                    value={form.error_field || ''}
                    onChange={(e) => onChange('error_field', e.target.value)}
                    disabled={readOnly}
                    style={standardInputStyles}
                  >
                    <option value="">— None —</option>
                    {form.error_field &&
                      !errorFieldOptions.some((o) => o.api === form.error_field) && (
                        <option value={form.error_field}>
                          {form.error_field} (stored)
                        </option>
                      )}
                    {errorFieldOptions.map((o) => (
                      <option key={o.api} value={o.api}>
                        {o.label} ({o.api})
                      </option>
                    ))}
                  </Form.Select>
                )}
                <Form.Text className="text-muted small d-block" style={{ fontSize: '0.72rem' }}>
                  Optional field for UI error highlight.
                </Form.Text>
                {!loadingFields && objectFields.length === 0 && (
                  <Form.Text className="text-warning small d-block">No fields loaded.</Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>

          <div className="mb-2 border-top pt-2">
            <Form.Label className="mb-1 fw-semibold small" style={standardLabelStyles}>
              Rule context <span className="fw-normal text-muted">(optional)</span>
            </Form.Label>
            <p className="text-muted small mb-2" style={{ fontSize: '0.75rem', lineHeight: 1.35 }}>
              Who the rule applies to. <code className="small">rule_context.grants</code>.
            </p>
            <Row className="g-2 align-items-end mb-1">
              <Col md={3}>
                <Form.Group className="mb-0">
                  <Form.Label className="small mb-1">Type</Form.Label>
                  <Form.Select
                    size="sm"
                    style={standardInputStyles}
                    value={contextPickerType}
                    onChange={(e) => {
                      setContextPickerType(e.target.value);
                      setContextLookupNonce((n) => n + 1);
                    }}
                    disabled={readOnly}
                  >
                    <option value="USER">User</option>
                    <option value="ROLE">Role</option>
                    <option value="TEAM">User group</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={9}>
                <Form.Group className="mb-0">
                  <Form.Label className="small mb-1">Add</Form.Label>
                  <div style={standardInputContainerHeight}>
                    <LookupLayout
                      key={`ctx-${contextPickerType}-${contextLookupNonce}`}
                      rootClassName="object-validation-context-lookup"
                      objectName={contextPickerLookup.objectName}
                      searchFields={contextPickerLookup.searchFields}
                      displayFields={contextPickerLookup.displayFields}
                      value=""
                      defaultLabel=""
                      onChange={onContextPickerSelect}
                      placeholder={contextPickerLookup.placeholder}
                      disabled={readOnly}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>
            {ruleContextDupHint && (
              <Form.Text className="text-warning small d-block mb-1">{ruleContextDupHint}</Form.Text>
            )}
            <Form.Group className="mb-0">
              <Form.Label
                className="d-block small mb-1 fw-semibold"
                style={standardLabelStyles}
                htmlFor="object-validation-selected-rule-context"
              >
                Selected
              </Form.Label>
              <div
                id="object-validation-selected-rule-context"
                className="rounded border p-1 px-2 bg-light"
                style={{ minHeight: 36 }}
              >
                <div className="d-block" style={{ lineHeight: 1.45 }}>
                  {ruleContextGrants.map((g, idx) => {
                    const chip = getContextGrantChipBadgeProps(g.target_type);
                    const t = normalizeContextTargetType(g.target_type);
                    const closeBtnClass =
                      t === 'TEAM'
                        ? 'p-0 d-inline-flex align-items-center justify-content-center text-dark'
                        : 'p-0 d-inline-flex align-items-center justify-content-center text-white text-opacity-75';
                    return (
                      <Badge
                        key={`${g.target_type}-${g.target_id}-${idx}`}
                        bg={chip.bg}
                        text={chip.text}
                        className={`${chip.className} me-1 mb-1 align-middle`}
                      >
                        <span className="me-1 text-break">{contextGrantChipLabel(g)}</span>
                        <Button
                          type="button"
                          variant="link"
                          className={closeBtnClass}
                          size="sm"
                          style={{ minWidth: 22, minHeight: 22 }}
                          onClick={() => removeRuleContextGrant(idx)}
                          disabled={readOnly}
                          aria-label="Remove from rule context"
                        >
                          <FaTimes className="small" style={{ fontSize: '0.65rem' }} />
                        </Button>
                      </Badge>
                    );
                  })}
                </div>
                {ruleContextGrants.length === 0 && (
                  <p className="text-muted small mb-0" style={{ fontSize: '0.8rem' }}>
                    None — rule applies to everyone.
                  </p>
                )}
              </div>
            </Form.Group>
          </div>

          <div className="row g-2 mb-0">
            <div className="col-md-4">
              <Form.Group className="mb-0">
                <Form.Label className="small mb-1">Execution order</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  value={form.execution_order}
                  onChange={(e) =>
                    onChange('execution_order', parseInt(e.target.value, 10) || 0)
                  }
                  disabled={readOnly}
                  min={0}
                />
              </Form.Group>
            </div>
            <div className="col-md-8 d-flex align-items-end flex-wrap gap-3 pb-1">
              <Form.Check
                type="checkbox"
                id="ov-active"
                className="small"
                label="Active"
                checked={form.is_active}
                onChange={(e) => onChange('is_active', e.target.checked)}
                disabled={readOnly}
              />
              <Form.Check
                type="checkbox"
                id="ov-stop"
                className="small"
                label="Stop on failure"
                checked={form.stop_on_failure}
                onChange={(e) => onChange('stop_on_failure', e.target.checked)}
                disabled={readOnly}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || readOnly}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ObjectValidationManager;
